import { useState, useEffect, FormEvent } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { useCreateProduct } from '../hooks/useProducts';
import { useWishlists } from '../hooks/useWishlists';

interface AddProductFormProps {
  fixedWishlistId?: string;
}

export default function AddProductForm({ fixedWishlistId }: AddProductFormProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [wishlistId, setWishlistId] = useState(fixedWishlistId ?? '');
  const createProduct = useCreateProduct();
  const { data: wishlists } = useWishlists();

  // Pre-select the default wishlist once loaded (only when not fixed)
  const defaultWishlist = wishlists?.find((wl) => wl.is_default);
  useEffect(() => {
    if (!fixedWishlistId && !wishlistId && defaultWishlist) {
      setWishlistId(defaultWishlist.id);
    }
  }, [defaultWishlist, wishlistId, fixedWishlistId]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    createProduct.mutate(
      {
        name: name.trim(),
        normalized_url: url.trim() || undefined,
        target_price: targetPrice ? parseFloat(targetPrice) : undefined,
        wishlist_id: wishlistId || undefined,      },
      {
        onSuccess: () => {
          setName('');
          setUrl('');
          setTargetPrice('');
        },
      }
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-border bg-card p-6 space-y-4"
    >
      <h3 className="text-lg font-medium text-card-foreground">Add Product</h3>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1">
          <label htmlFor="product-name" className="text-sm font-medium text-foreground">
            Name *
          </label>
          <input
            id="product-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sony WH-1000XM5"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="product-url" className="text-sm font-medium text-foreground">
            URL
          </label>
          <input
            id="product-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="target-price" className="text-sm font-medium text-foreground">
            Target Price
          </label>
          <input
            id="target-price"
            type="number"
            min="0"
            step="0.01"
            value={targetPrice}
            onChange={(e) => setTargetPrice(e.target.value)}
            placeholder="$0.00"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>

        {!fixedWishlistId && (
          <div className="space-y-1">
            <label htmlFor="wishlist" className="text-sm font-medium text-foreground">
              Wishlist
            </label>
            <select
              id="wishlist"
              value={wishlistId}
              onChange={(e) => setWishlistId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">None</option>
              {wishlists?.map((wl) => (
                <option key={wl.id} value={wl.id}>
                  {wl.name}{wl.is_default ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {createProduct.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          Failed to add product. Please try again.
        </div>
      )}

      <button
        type="submit"
        disabled={createProduct.isPending || !name.trim()}
        className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
      >
        {createProduct.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Plus className="h-4 w-4" />
        )}
        Add Product
      </button>
    </form>
  );
}
